import { css } from '@emotion/react';
import { theme } from '@frontend/theme';
import { getThemeFontColor } from './utils';

export interface FieldWrapperStyleProps {
  active?: boolean;
  disabled?: boolean;
  hasPadding?: boolean;
  hasError?: boolean;
}

type GetBorderColorProps = {
  active?: boolean;
  disabled?: boolean;
  hasError?: boolean;
};

const getBorderColorTheme = ({ active, hasError, disabled }: GetBorderColorProps) => {
  if (disabled) return theme.colors.neutral20;
  if (hasError) return theme.colors.status.critical;
  if (active) return theme.colors.primary50;
  return theme.colors.neutral30;
};

const styles = ({ active, disabled, hasError, hasPadding }: FieldWrapperStyleProps) => {
  const borderColor = theme.colors.neutral30;
  const borderRadius = theme.borderRadius.small;
  const color = theme.colors.neutral90;
  const spacing = theme.spacing;
  const fontFamily = theme.font.family;
  const fontSize = theme.fontSize;
  const disabledColor = theme.colors.neutral20;
  const contextualBorderColorValue = getBorderColorTheme({ active, disabled, hasError });
  const placeholderColorValue = getThemeFontColor({ theme, disabled, hasError });

  return [
    css`
      height: 40px;
      border: 1px solid ${borderColor};
      border-radius: ${borderRadius};
      color: ${color};
      display: flex;
      align-items: center;
      margin: 0;

      position: relative;
      transition: outline-color 250ms ease-out;
      outline-color: transparent;
      border-color: ${contextualBorderColorValue};

      @supports (selector(:has(*))) {
        &:has(:is(input, textarea):focus) {
          box-shadow: 0 0 0 1px ${contextualBorderColorValue};
          // Outlines only respect the border radius in Chrome right now
          /* outline: solid 1px ${contextualBorderColorValue}; */
        }
      }

      @supports not (selector(:has(*))) {
        /**
       * Unfortunately, Firefox does not support :has without a feature flag.
       * This means that Firefox users will see the outline around the input and any other internal elements that have outlines
      **/
        &:focus-within {
          /* outline: solid 1px ${contextualBorderColorValue}; */
          box-shadow: 0 0 0 1px ${contextualBorderColorValue};
        }
      }

      input,
      textarea {
        width: 100%;
        background: transparent;
        border: none;
        color: ${disabled ? disabledColor : color};
        display: block;
        font-family: ${fontFamily};
        font-size: ${fontSize(16)};
        line-height: 1.375;
        text-align: left;
        -webkit-appearance: none;

        :focus {
          outline: none;
        }
      }

      input::placeholder,
      textarea::placeholder {
        color: ${placeholderColorValue};
      }

      /* anything other than a label before an input? needs left margin */
      > *:not(label) + input {
        margin-left: ${spacing(1)};
      }
      /* if the input isn't the last child, it needs right margin */
      input:not(:last-child) {
        margin-right: ${spacing(1)};
      }
    `,
    disabled &&
      css`
        svg {
          color: ${disabledColor};
        }
      `,
    hasPadding &&
      css`
        padding: 0;
        padding: ${spacing(0, 2)};
      `,
  ];
};

export const FieldWrapperStyles = {
  fieldWrapper: (fieldWrapperProps: FieldWrapperStyleProps) => styles(fieldWrapperProps),
};
